import { useTranslation } from 'react-i18next';
import {
  SalesProductRanking,
  type SalesBreakdownRow,
  type SalesFiltersApplied,
} from '@optiflow/types';
import { Card } from '@/components/ui/Card';

export interface SalesFilterValue {
  operatorId: string;
  categoryId: string;
  brandId: string;
  ranking: SalesProductRanking;
}

/**
 * The Sales-only filters.
 *
 * The options are taken from the report the reader is already looking at, rather than from the
 * catalogue and user lists: it needs no permission the Sales report does not already require, and
 * it offers exactly the operators and classifications that sold something in this period. A row
 * with no id — an operator who is no longer a user, a product with no category — is not offerable,
 * so it is skipped here while still counting in the report itself.
 *
 * Every id is re-resolved and re-checked server-side; `applied` is what the server says it used.
 */
export function SalesFilterBar({
  value,
  applied,
  operators,
  categories,
  brands,
  onChange,
}: {
  value: SalesFilterValue;
  applied: SalesFiltersApplied | null;
  operators: SalesBreakdownRow[];
  categories: SalesBreakdownRow[];
  brands: SalesBreakdownRow[];
  onChange: (next: Partial<SalesFilterValue>) => void;
}) {
  const { t } = useTranslation();
  const field = 'border-line bg-surface text-ink w-full rounded-lg border px-3 py-2 text-sm';

  const options = (rows: SalesBreakdownRow[]) =>
    rows.filter((row): row is SalesBreakdownRow & { id: string } => row.id !== null);

  const select = (
    id: string,
    label: string,
    hint: string | undefined,
    chosen: string,
    rows: SalesBreakdownRow[],
    anyLabel: string,
    onPick: (next: string) => void,
    unnamedLabel?: string,
  ) => (
    <div>
      <label htmlFor={id} className="text-muted mb-1 block text-xs font-medium">
        {label}
      </label>
      <select id={id} className={field} value={chosen} onChange={(e) => onPick(e.target.value)}>
        <option value="">{anyLabel}</option>
        {options(rows).map((row) => (
          <option key={row.id} value={row.id}>
            {row.label ?? unnamedLabel ?? row.id}
          </option>
        ))}
      </select>
      {hint ? <p className="text-muted mt-1 text-[11px]">{hint}</p> : null}
    </div>
  );

  const cleared = !value.operatorId && !value.categoryId && !value.brandId;

  return (
    <Card className="p-4">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {select(
          'sales-operator',
          t('reports.sales.filters.operator'),
          t('reports.sales.hints.operator'),
          value.operatorId,
          operators,
          t('reports.sales.filters.allOperators'),
          (next) => onChange({ operatorId: next }),
          t('reports.sales.records.unknownOperator'),
        )}
        {select(
          'sales-category',
          t('reports.sales.filters.category'),
          t('reports.sales.hints.currentCatalogue'),
          value.categoryId,
          categories,
          t('reports.sales.filters.allCategories'),
          (next) => onChange({ categoryId: next }),
        )}
        {select(
          'sales-brand',
          t('reports.sales.filters.brand'),
          t('reports.sales.hints.currentCatalogue'),
          value.brandId,
          brands,
          t('reports.sales.filters.allBrands'),
          (next) => onChange({ brandId: next }),
        )}

        <div>
          <label htmlFor="sales-ranking" className="text-muted mb-1 block text-xs font-medium">
            {t('reports.sales.filters.ranking')}
          </label>
          <select
            id="sales-ranking"
            className={field}
            value={value.ranking}
            onChange={(event) => onChange({ ranking: event.target.value as SalesProductRanking })}
          >
            <option value={SalesProductRanking.NET_SALES}>
              {t('reports.sales.filters.byNetSales')}
            </option>
            <option value={SalesProductRanking.QUANTITY}>
              {t('reports.sales.filters.byQuantity')}
            </option>
          </select>
          <button
            type="button"
            className="text-brand-700 mt-2 text-sm font-medium hover:underline disabled:cursor-not-allowed disabled:opacity-50"
            disabled={cleared}
            onClick={() => onChange({ operatorId: '', categoryId: '', brandId: '' })}
          >
            {t('reports.sales.filters.clear')}
          </button>
        </div>
      </div>

      {applied && (applied.operatorName || applied.categoryName || applied.brandName) ? (
        <p className="text-muted mt-3 text-xs">
          {t('reports.sales.filters.applied', {
            names: [applied.operatorName, applied.categoryName, applied.brandName]
              .filter(Boolean)
              .join(' · '),
          })}
        </p>
      ) : null}
    </Card>
  );
}
